/** Strict browser boundary for route-sized Measurements snapshots. */
import { z } from 'zod';
import type {
  MeasurementCampaignSnapshot as MeasurementCampaignSnapshotContract,
  MeasurementDashboardSnapshot as MeasurementDashboardSnapshotContract,
  MeasurementStandContextSnapshot as MeasurementStandContextSnapshotContract,
} from '@/types/generated/forest_models';
import {
  measurementCampaignMapContextSchema,
  measurementCampaignPlotSchema,
  measurementCampaignProvenanceSchema,
} from '@/api/measurementCampaignEntity.schemas';
export {
  measurementCampaignMapContextSchema,
  measurementPlotGeometrySchema,
} from '@/api/measurementCampaignEntity.schemas';
const reasonCodes = z.array(z.string().min(1));
export const measurementSnapshotEnvelope = {
  schema_version: z.literal('2.0'), generated_at: z.iso.datetime(),
  snapshot_revision: z.string().regex(/^[a-f0-9]{64}$/),
  status: z.enum(['available', 'partial', 'empty', 'unavailable']),
  reason_codes: reasonCodes,
};
const entitlement = z.strictObject({
  package_state: z.enum(['active', 'expired', 'locked']),
  read_allowed: z.boolean(),
  commit_allowed: z.boolean(),
  reason_codes: reasonCodes,
  grant_origin: z.enum(['offer_decode', 'plot_package', 'portfolio_estate', 'administrator', 'none']),
  grant_expires_at: z.iso.datetime().nullable(),
});
export const measurementPropertyIdentitySchema = z.strictObject({
  property_id: z.string().min(1),
  property_name: z.string().min(1).nullable(),
  municipality: z.string().nullable(),
  entitlement,
});
const changedMetric = z.strictObject({
  metric: z.enum([
    'volume_m3_ha',
    'basal_area_m2_ha',
    'mean_diameter_cm',
    'mean_height_m',
    'stems_per_ha',
  ]),
  baseline: z.number(),
  resolved: z.number(),
  unit: z.string().min(1),
  absolute_change: z.number(),
  relative_change_pct: z.number().nullable(),
  change_source: z.enum([
    'metsakeskus',
    'field_scan',
    'manual',
    'correction',
    'stratum_correction',
  ]),
});
const measurementSource = z.enum(['metsakeskus', 'field_scan', 'manual']);
const measurementSourceIdentity = z.enum(['public_estimate', 'phone_scan', 'professional_inventory', 'owner_relascope']);
export const measurementSourceCampaignOptionSchema = z.strictObject({
  value: z.number().int().positive(), label: z.string().min(1),
});
const standSourceOptionFields = {
  stand_id: z.string().min(1),
  stand_number: z.string().min(1).nullable(),
  field_scan_options: z.array(measurementSourceCampaignOptionSchema),
  manual_options: z.array(measurementSourceCampaignOptionSchema),
  available_sources: z.array(measurementSource),
};
export const measurementLargestChangeSchema = changedMetric.extend({
  stand_id: z.string().min(1), stand_number: z.string().min(1).nullable(),
});
export const measurementStandMetricsSchema = z.strictObject({
  volume_m3_ha: z.number().nullable(), basal_area_m2_ha: z.number().nullable(),
  mean_diameter_cm: z.number().nullable(), mean_height_m: z.number().nullable(),
  stems_per_ha: z.number().nullable(),
});
const measurementMetricEvidenceSchema = z.strictObject({
  status: z.enum(['available', 'unavailable']),
  source: z.enum([
    'metsakeskus',
    'field_scan',
    'manual',
    'correction',
    'stratum_correction',
  ]).nullable(),
  reason_codes: reasonCodes,
}).superRefine((value, context) => {
  const valid = value.status === 'available'
    ? value.source !== null && value.reason_codes.length === 0
    : value.source === null && value.reason_codes.length > 0;
  if (!valid) {
    context.addIssue({
      code: 'custom',
      message: 'Metric evidence availability is contradictory',
    });
  }
});
export const measurementStandMetricEvidenceSchema = z.strictObject({
  volume_m3_ha: measurementMetricEvidenceSchema,
  basal_area_m2_ha: measurementMetricEvidenceSchema,
  mean_diameter_cm: measurementMetricEvidenceSchema,
  mean_height_m: measurementMetricEvidenceSchema,
  stems_per_ha: measurementMetricEvidenceSchema,
});
export const measurementValuationReadinessSchema = z.strictObject({
  status: z.enum(['available', 'unavailable']),
  reason_codes: reasonCodes,
  // Present only with reason `manual_measurement_incomplete`: the stratum
  // fields the owner still has to enter.
  missing_fields: z.array(z.string().min(1)).optional(),
}).superRefine((value, context) => {
  const valid = value.status === 'available'
    ? value.reason_codes.length === 0
    : value.reason_codes.length > 0;
  if (!valid) {
    context.addIssue({
      code: 'custom',
      message: 'Valuation readiness availability is contradictory',
    });
  }
});
export const measurementDashboardPropertySchema = z.strictObject({
  property_id: z.string().min(1),
  property_name: z.string().min(1).nullable(),
  municipality: z.string().nullable(),
  status: z.enum(['available', 'partial', 'empty']),
  reason_codes: reasonCodes,
  campaign_count: z.number().int().nonnegative(),
  measured_stands: z.number().int().nonnegative(),
  public_estimate_stands: z.number().int().nonnegative(),
  total_stands: z.number().int().nonnegative(),
  stands: z.array(z.strictObject({
    ...standSourceOptionFields,
    effective_source: measurementSource,
    effective_campaign_id: z.number().int().positive().nullable(),
    resolved_metrics: measurementStandMetricsSchema,
    metric_evidence: measurementStandMetricEvidenceSchema,
    valuation_readiness: measurementValuationReadinessSchema,
    simulation_readiness: measurementValuationReadinessSchema,
    changed_metrics: z.array(changedMetric),
  })),
  attention_count: z.number().int().nonnegative(),
  latest_campaign_id: z.number().int().positive().nullable(),
  latest_measurement_date: z.iso.date().nullable(),
  latest_source: z.strictObject({
    campaign_id: z.number().int().positive(),
    tool: z.string().nullable(),
    tool_version: z.string().nullable(),
    measurement_date: z.iso.date().nullable(),
  }).nullable(),
  largest_change: measurementLargestChangeSchema.nullable(),
  entitlement,
});

export const measurementDashboardCampaignSchema = z.strictObject({
  id: z.number().int().positive(),
  family_id: z.uuid(),
  linked_partitions: z.array(z.strictObject({
    campaign_id: z.number().int().positive(),
    property_id: z.string().min(1),
    property_name: z.string().min(1).nullable(),
  })),
  property_id: z.string().min(1),
  property_name: z.string().min(1).nullable(),
  name: z.string().min(1),
  measurement_date: z.iso.date().nullable(),
  year: z.number().int().nullable(),
  tool: z.string(),
  tool_version: z.string(),
  measurement_source: z.enum(['field_scan', 'manual']),
  status: z.enum(['draft', 'active', 'validated', 'archived']),
  processing_status: z.enum(['pending', 'processing', 'complete', 'failed']),
  error_message: z.string(),
  parameters_hash: z.string(),
  plot_count: z.number().int().nonnegative(),
  tree_count: z.number().int().nonnegative(),
  mapping_attention_count: z.number().int().nonnegative(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});

export const measurementDashboardSnapshotSchema: z.ZodType<MeasurementDashboardSnapshotContract> = z.strictObject({
  ...measurementSnapshotEnvelope,
  property_count: z.number().int().nonnegative(),
  measured_stands: z.number().int().nonnegative(),
  public_estimate_stands: z.number().int().nonnegative(),
  total_stands: z.number().int().nonnegative(),
  attention_count: z.number().int().nonnegative(),
  largest_change: measurementLargestChangeSchema.nullable(),
  properties: z.array(measurementDashboardPropertySchema),
  campaigns: z.array(measurementDashboardCampaignSchema),
});

const distribution = z.strictObject({
  status: z.enum(['available', 'modelled_only', 'unavailable']),
  reason_codes: reasonCodes,
  baseline: z.array(z.strictObject({
    species_code: z.number().int(),
    species_name: z.string(),
    bins: z.array(z.strictObject({
      diameter_class: z.number(),
      stems_per_ha: z.number(),
    })),
  })).nullable(),
  resolved: z.array(z.strictObject({
    species_code: z.number().int(),
    species_name: z.string(),
    bins: z.array(z.strictObject({
      diameter_class: z.number(),
      stems_per_ha: z.number(),
    })),
  })).nullable(),
}).superRefine((value, context) => {
  const valid = value.status === 'available'
    ? Boolean(value.resolved?.length && value.reason_codes.length === 0)
    : value.status === 'modelled_only'
      ? Boolean(value.baseline?.length && value.resolved === null && value.reason_codes.length > 0)
      : value.baseline === null && value.resolved === null && value.reason_codes.length > 0;
  if (!valid) {
    context.addIssue({
      code: 'custom',
      message: 'Distribution availability payload is contradictory',
    });
  }
});

const accuracyEvidence = z.strictObject({
  status: z.enum(['available', 'unavailable']),
  reason_codes: reasonCodes,
  evidence_version: z.string().nullable(),
  citation: z.strictObject({
    id: z.string().min(1),
    title: z.string().min(1),
    publisher: z.string().min(1),
    publication_date: z.iso.date(),
    url: z.url(),
    locator: z.string().min(1),
    retrieved_date: z.iso.date(),
  }).nullable(),
  limitations: z.array(z.string().min(1)),
}).superRefine((value, context) => {
  const hasAnyReference = value.evidence_version !== null
    || value.citation !== null
    || value.limitations.length > 0;
  const hasCompleteReference = Boolean(
    value.evidence_version
    && value.citation
    && value.limitations.length,
  );
  const valid = value.status === 'available'
    ? Boolean(
      hasCompleteReference
      && value.reason_codes.length === 0,
    )
    : value.reason_codes.length > 0
      && (!hasAnyReference || hasCompleteReference);
  if (!valid) {
    context.addIssue({
      code: 'custom',
      message: 'Accuracy evidence availability payload is contradictory',
    });
  }
});

export const measurementSourceProvenanceSchema = z.strictObject({
  source: measurementSource,
  campaign_id: z.number().int().positive().nullable(),
  tool: z.string().nullable(),
  tool_version: z.string().nullable(),
  measurement_date: z.iso.date().nullable(),
  observed_at: z.iso.datetime().nullable(),
  retrieved_at: z.iso.datetime().nullable(),
  checksum: z.string().nullable(),
  schema_version: z.string().nullable(),
  stale: z.boolean().nullable(),
  reason_codes: reasonCodes,
});

const activation = z.strictObject({
  eligible: z.boolean(),
  blocker_codes: reasonCodes,
  requires_confirmation: z.literal(true),
});

export const measurementStandSnapshotSchema = z.strictObject({
  stand_id: z.string().min(1),
  stand_number: z.string().min(1).nullable(),
  status: z.enum(['available', 'partial']),
  reason_codes: reasonCodes,
  effective_source: measurementSource,
  source_identity: measurementSourceIdentity.nullable(),
  available_sources: z.array(measurementSource),
  effective_campaign_id: z.number().int().positive().nullable(),
  comparison_source: measurementSource,
  comparison_campaign_id: z.number().int().positive().nullable(),
  baseline_metrics: measurementStandMetricsSchema,
  resolved_metrics: measurementStandMetricsSchema,
  metric_evidence: measurementStandMetricEvidenceSchema,
  valuation_readiness: measurementValuationReadinessSchema,
  simulation_readiness: measurementValuationReadinessSchema,
  changed_metrics: z.array(changedMetric),
  distribution_comparison: distribution,
  source_provenance: measurementSourceProvenanceSchema,
  activation,
}).superRefine((value, context) => {
  const identityMatches = value.source_provenance.source === value.comparison_source
    && value.source_provenance.campaign_id === value.comparison_campaign_id;
  const campaignShapeMatches = value.comparison_source === 'metsakeskus'
    ? value.comparison_campaign_id === null
    : value.comparison_source === 'field_scan'
      ? value.comparison_campaign_id !== null
      : true;
  if (!identityMatches || !campaignShapeMatches) {
    context.addIssue({
      code: 'custom',
      message: 'Comparison identity must match its served provenance',
    });
  }
});

const campaignList = z.strictObject({
  id: z.number().int().positive(),
  property_id: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  measurement_date: z.iso.date().nullable(),
  year: z.number().int().nullable(),
  tool: z.string(),
  tool_version: z.string(),
  status: z.enum(['draft', 'active', 'validated', 'archived']),
  processing_status: z.enum(['pending', 'processing', 'complete', 'failed']),
  error_message: z.string(),
  parameters_hash: z.string(),
  processing_started_at: z.iso.datetime().nullable(),
  processing_completed_at: z.iso.datetime().nullable(),
  plot_count: z.number().int().nonnegative(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});

const standSourceOptions = z.strictObject(standSourceOptionFields);

const sourceActivation = z.strictObject({
  action_key: z.literal('measurements.source-activation'),
  status: z.enum(['available', 'locked']),
  reason_codes: reasonCodes,
  requires_package: z.boolean(),
  requires_confirmation: z.boolean(),
});

export const measurementStandContextSnapshotSchema: z.ZodType<MeasurementStandContextSnapshotContract> = z.strictObject({
  ...measurementSnapshotEnvelope,
  property: measurementPropertyIdentitySchema,
  stand: measurementStandSnapshotSchema,
  source_options: standSourceOptions,
  accuracy_evidence: accuracyEvidence,
  source_activation: sourceActivation,
}).superRefine((value, context) => {
  if (value.stand.stand_id !== value.source_options.stand_id) {
    context.addIssue({
      code: 'custom',
      message: 'Stand source options must match the served stand context',
    });
  }
});

export const measurementCampaignSnapshotSchema: z.ZodType<MeasurementCampaignSnapshotContract> = z.strictObject({
  ...measurementSnapshotEnvelope,
  property: measurementPropertyIdentitySchema,
  campaign: campaignList.extend({
    family_id: z.uuid(),
    import_source: z.string(),
    provenance: measurementCampaignProvenanceSchema,
  }),
  map_context: measurementCampaignMapContextSchema,
  summary: z.strictObject({
    plot_count: z.number().int().nonnegative(),
    tree_count: z.number().int().nonnegative(),
    measured_stands: z.number().int().nonnegative(),
    active_stands: z.number().int().nonnegative(),
    blocked_stands: z.number().int().nonnegative(),
    unmapped_plots: z.number().int().nonnegative(),
    mapping_attention_plots: z.number().int().nonnegative(),
    largest_change: measurementLargestChangeSchema.nullable(),
  }),
  plots: z.array(measurementCampaignPlotSchema),
  stands: z.array(measurementStandSnapshotSchema),
  mapping_targets: z.array(z.strictObject({
    stand_id: z.string().min(1),
    stand_number: z.string().min(1).nullable(),
  })),
  target_properties: z.array(z.strictObject({
    property_id: z.string().min(1),
    property_name: z.string().min(1).nullable(),
    municipality: z.string().nullable(),
  })),
  activation_proposal: z.strictObject({
    prior_sources: z.array(z.strictObject({
      stand_id: z.string().min(1),
      stand_number: z.string().min(1).nullable(),
      source: measurementSource,
      campaign_id: z.number().int().positive().nullable(),
    })),
    new_source: z.enum(['field_scan', 'manual']),
    campaign_id: z.number().int().positive(),
    affected_stand_ids: z.array(z.string().min(1)),
    eligible: z.boolean(),
    blocker_codes: reasonCodes,
    requires_confirmation: z.literal(true),
  }),
}).superRefine((value, context) => {
  if (value.stands.some((candidate) => (
    candidate.comparison_source !== value.activation_proposal.new_source
    || candidate.comparison_campaign_id !== value.campaign.id
  ))) {
    context.addIssue({
      code: 'custom',
      message: 'Campaign stand comparisons must match the snapshot campaign',
    });
  }
  const mappingTargetIds = value.mapping_targets.map(({ stand_id }) => stand_id);
  if (new Set(mappingTargetIds).size !== mappingTargetIds.length
    || value.stands.some(({ stand_id }) => !mappingTargetIds.includes(stand_id))) {
    context.addIssue({
      code: 'custom',
      message: 'Campaign mapping targets must be unique and contain every campaign stand',
    });
  }
});

export type MeasurementDashboardSnapshot = MeasurementDashboardSnapshotContract;
export type MeasurementCampaignSnapshot = MeasurementCampaignSnapshotContract;
export type MeasurementStandContextSnapshot = MeasurementStandContextSnapshotContract;
